// src/utils/exportToPDF.js
/**
 * Export analysis results to PDF
 * This function sends the data to a backend endpoint that generates the PDF using reportlab
 */

export async function exportAnalysisToPDF(results, resumeName, jobTitle) {
  try {
    // Create a formatted payload
    const payload = {
      resumeName: resumeName || 'Resume',
      jobTitle: jobTitle || 'Position',
      overallScore: results.overallScore || 0,
      overallFeedback: results.overallFeedback || '',
      matchedKeywords: results.matchedKeywords || [],
      missingKeywords: results.missingKeywords || [],
      suggestions: results.suggestions || [],
      strengths: results.strengths || [],
      weaknesses: results.weaknesses || [],
      timestamp: new Date().toLocaleDateString()
    };

    // Since we don't have a backend, we'll use a Python script approach
    // For now, create a downloadable HTML version that can be printed to PDF
    const htmlContent = generatePDFHTML(payload);
    
    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Resume_Analysis_${Date.now()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('PDF export error:', error);
    return { success: false, error: error.message };
  }
}

function generatePDFHTML(data) {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Resume Analysis Report</title>
  <style>
    @media print {
      body { margin: 0; }
      .no-print { display: none; }
    }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 40px 20px;
      color: #1f2937;
      line-height: 1.6;
    }
    .header {
      text-align: center;
      margin-bottom: 40px;
      border-bottom: 3px solid #06b6d4;
      padding-bottom: 20px;
    }
    .header h1 {
      margin: 0;
      color: #0891b2;
      font-size: 32px;
    }
    .header p {
      color: #6b7280;
      margin: 10px 0;
    }
    .score-section {
      background: linear-gradient(135deg, #06b6d4 0%, #10b981 100%);
      color: white;
      padding: 30px;
      border-radius: 12px;
      text-align: center;
      margin-bottom: 30px;
    }
    .score {
      font-size: 64px;
      font-weight: bold;
      margin: 0;
    }
    .score-label {
      font-size: 18px;
      margin-top: 10px;
      opacity: 0.9;
    }
    .section {
      margin-bottom: 30px;
      padding: 20px;
      background: #f9fafb;
      border-radius: 8px;
      border-left: 4px solid #06b6d4;
    }
    .section h2 {
      color: #0891b2;
      margin-top: 0;
      font-size: 24px;
    }
    .keyword-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 10px;
      margin-top: 15px;
    }
    .keyword {
      background: white;
      padding: 8px 12px;
      border-radius: 6px;
      border: 1px solid #e5e7eb;
      font-size: 14px;
    }
    .keyword.matched {
      background: #d1fae5;
      border-color: #10b981;
      color: #065f46;
    }
    .keyword.missing {
      background: #fee2e2;
      border-color: #ef4444;
      color: #991b1b;
    }
    ul {
      list-style: none;
      padding: 0;
    }
    li {
      padding: 12px;
      background: white;
      margin-bottom: 10px;
      border-radius: 6px;
      border-left: 3px solid #06b6d4;
    }
    .feedback {
      background: white;
      padding: 20px;
      border-radius: 8px;
      border: 1px solid #e5e7eb;
      margin-top: 15px;
      white-space: pre-wrap;
      line-height: 1.8;
    }
    .print-btn {
      background: #06b6d4;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 16px;
      cursor: pointer;
      display: block;
      margin: 30px auto;
      font-weight: 600;
    }
    .print-btn:hover {
      background: #0891b2;
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>📄 Resume Analysis Report</h1>
    <p><strong>Resume:</strong> ${data.resumeName}</p>
    <p><strong>Position:</strong> ${data.jobTitle}</p>
    <p><strong>Analysis Date:</strong> ${data.timestamp}</p>
  </div>

  <button class="print-btn no-print" onclick="window.print()">🖨️ Print or Save as PDF</button>

  <div class="score-section">
    <div class="score">${data.overallScore}%</div>
    <div class="score-label">Overall Match Score</div>
  </div>

  ${data.overallFeedback ? `
  <div class="section">
    <h2>📊 Overall Assessment</h2>
    <div class="feedback">${data.overallFeedback}</div>
  </div>
  ` : ''}

  ${data.matchedKeywords && data.matchedKeywords.length > 0 ? `
  <div class="section">
    <h2>✅ Matched Keywords (${data.matchedKeywords.length})</h2>
    <div class="keyword-grid">
      ${data.matchedKeywords.map(kw => `<div class="keyword matched">${kw}</div>`).join('')}
    </div>
  </div>
  ` : ''}

  ${data.missingKeywords && data.missingKeywords.length > 0 ? `
  <div class="section">
    <h2>❌ Missing Keywords (${data.missingKeywords.length})</h2>
    <div class="keyword-grid">
      ${data.missingKeywords.map(kw => `<div class="keyword missing">${kw}</div>`).join('')}
    </div>
  </div>
  ` : ''}

  ${data.strengths && data.strengths.length > 0 ? `
  <div class="section">
    <h2>💪 Strengths</h2>
    <ul>
      ${data.strengths.map(s => `<li>${s}</li>`).join('')}
    </ul>
  </div>
  ` : ''}

  ${data.weaknesses && data.weaknesses.length > 0 ? `
  <div class="section">
    <h2>⚠️ Areas for Improvement</h2>
    <ul>
      ${data.weaknesses.map(w => `<li>${w}</li>`).join('')}
    </ul>
  </div>
  ` : ''}

  ${data.suggestions && data.suggestions.length > 0 ? `
  <div class="section">
    <h2>💡 Recommendations</h2>
    <ul>
      ${data.suggestions.map(s => `<li>${s}</li>`).join('')}
    </ul>
  </div>
  ` : ''}

  <div style="text-align: center; margin-top: 50px; padding-top: 30px; border-top: 2px solid #e5e7eb; color: #6b7280; font-size: 14px;">
    <p>Generated by <strong>SmartResumeTool</strong></p>
    <p>AI-Powered Resume Analysis</p>
  </div>
</body>
</html>
  `.trim();
}